/**
 * Hook de status para WhatsApp Cloud API.
 * Consome WhatsAppCloudApiService.getStatus + integra com ChannelAlertsContext
 * para refletir alertas em tempo real (badge, alertSeverity).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import WhatsAppCloudApiService from '../../../../WhatsAppCloudApi/services/WhatsAppCloudApiService';
import { useChannelAlerts } from '../../../contexts/ChannelAlertsContext';

const QUALITY_VARIANT_MAP = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'danger',
  UNKNOWN: 'neutral',
  '': 'neutral',
};

const TIER_LABELS = {
  TIER_1K: '1K msg/dia',
  TIER_10K: '10K msg/dia',
  TIER_100K: '100K msg/dia',
  TIER_UNLIMITED: 'ilimitado',
  '': '',
};

export default function useCloudApiStatus(agentId) {
  const [raw, setRaw] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelRef = useRef({ cancelled: false });

  const { alerts: cloudAlerts } = useChannelAlerts({ channelKey: 'cloud_api' });

  const fetchStatus = useCallback(async () => {
    if (!agentId || agentId === 'novo') {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await WhatsAppCloudApiService.getStatus(agentId);
      if (cancelRef.current.cancelled) return;
      setRaw(resp.data);
    } catch (err) {
      if (cancelRef.current.cancelled) return;
      setError(err.response?.data?.error || err.message || 'Erro ao buscar status');
      setRaw(null);
    } finally {
      if (!cancelRef.current.cancelled) setIsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    cancelRef.current = { cancelled: false };
    fetchStatus();
    return () => {
      cancelRef.current.cancelled = true;
    };
  }, [fetchStatus]);

  // Re-fetch quando recebe alerta novo deste canal (qualidade, status, etc.)
  // Tracking via length para não disparar em mark-as-read.
  const lastAlertLenRef = useRef(0);
  useEffect(() => {
    if (cloudAlerts.length > lastAlertLenRef.current) {
      lastAlertLenRef.current = cloudAlerts.length;
      fetchStatus();
    }
  }, [cloudAlerts.length, fetchStatus]);

  // Deriva representação consumida pelo card
  const isConnected = !!raw?.connected;
  const quality = raw?.quality_rating || '';
  const limit = raw?.messaging_limit || '';

  // Deriva alerta visual
  const unreadAlerts = cloudAlerts.filter((a) => !a.read);
  const hasErrorAlert = unreadAlerts.some((a) => a.severity === 'error');
  const hasWarningAlert = unreadAlerts.some((a) => a.severity === 'warning');

  let statusVariant = 'neutral';
  let statusLabel = 'Desconectado';

  if (isConnected) {
    statusVariant = QUALITY_VARIANT_MAP[quality] || 'success';
    statusLabel = 'Conectado';
    if (hasErrorAlert) {
      statusVariant = 'danger';
      statusLabel = 'Atenção crítica';
    } else if (hasWarningAlert) {
      statusVariant = 'warning';
      statusLabel = 'Com alertas';
    } else if (quality === 'RED') {
      statusVariant = 'danger';
      statusLabel = 'Qualidade RED';
    } else if (quality === 'YELLOW') {
      statusVariant = 'warning';
      statusLabel = 'Qualidade YELLOW';
    }
  }

  let summary = '';
  if (isConnected && raw) {
    const parts = [];
    if (raw.display_phone_number) parts.push(raw.display_phone_number);
    if (raw.business_name) parts.push(raw.business_name);
    summary = parts.join(' · ');
  }

  return {
    isConnected,
    isLoading,
    error,
    statusLabel,
    statusVariant,
    summary,
    hasAlert: hasErrorAlert || hasWarningAlert,
    alertSeverity: hasErrorAlert ? 'error' : hasWarningAlert ? 'warning' : null,
    refresh: fetchStatus,
    raw,
    qualityVariant: QUALITY_VARIANT_MAP[quality] || 'neutral',
    tierLabel: TIER_LABELS[limit] || limit,
  };
}
